import { BaseService } from '../base';
import {
  Broker,
  BrokerAdvisory,
  BrokerProduct,
  BrokerProductCategory,
  BulkUpdateBrokerProductsRequest,
  BulkUpdateBrokerProductsResult,
  BrokerProductsApiResponse,
  BrokersResponse,
  BrokersResponseRequest,
  BrokerUsersApiResponse,
  GetBrokersParams,
  GetBrokerUsersParams,
  SearchBrokersParams,
  UpdateBrokerProductDto,
  UpdateBrokerProductsResult,
} from './types';

export class BrokersService extends BaseService {
  private customBaseUrl = import.meta.env.VITE_API_URL;

  constructor() {
    super();
  }

  async listBrokers(params: GetBrokersParams = {}): Promise<{
    success: boolean;
    // Devolve `Broker[]`, não o cru da API: o método mapeia `advisoryId` antes
    // de retornar (ver `mappedBrokers` abaixo).
    data?: BrokersResponse;
    error?: string;
  }> {
    const {
      page = 1,
      pageSize = 10,
      search = '',
      advisoryId,
      city,
      state,
      sortField,
      sortType,
    } = params;

    try {
      const queryParams = new URLSearchParams();

      queryParams.append('pageNumber', page.toString());
      queryParams.append('pageSize', pageSize.toString());
      if (search) queryParams.append('filter', search);
      if (advisoryId) queryParams.append('advisoryId', advisoryId);
      if (city) queryParams.append('city', city);
      if (state) queryParams.append('state', state);
      if (sortField) queryParams.append('sortField', sortField);
      if (sortType) queryParams.append('sortType', sortType);

      const response = await this.get(
        `/portal/v1/parties/brokers?${queryParams.toString()}`,
        {},
        this.customBaseUrl
      );

      const json =
        await this.handleJsonResponse<BrokersResponseRequest>(response);

      if (json.success && json.data) {
        // Mapear os dados da API para garantir que advisoryId seja incluído
        const mappedBrokers = json.data.data.map(broker => ({
          ...broker,
          advisoryId:
            broker.advisoryId || broker.advisory_id || broker.partyId || '',
        })) as Broker[];

        return {
          ...json,
          data: {
            data: mappedBrokers,
            meta: json.data.meta,
          },
        };
      }

      // Só chega aqui sem `data` (falha ou corpo vazio); repassar o `json`
      // cru arrastaria o tipo da resposta bruta para dentro do contrato mapeado.
      return { success: json.success, error: json.error };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /**
   * Assessorias que aparecem como vínculo de alguma corretora. Serve ao filtro
   * da listagem: `GET /parties/agencies` devolveria o cadastro inteiro, com
   * assessorias que não filtram nada.
   */
  async listBrokerAdvisories(): Promise<{
    success: boolean;
    data?: BrokerAdvisory[];
    error?: string;
  }> {
    try {
      const response = await this.get(
        '/portal/v1/parties/brokers/advisories',
        {},
        this.customBaseUrl
      );

      return await this.handleJsonResponse<BrokerAdvisory[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar assessorias',
      };
    }
  }

  async searchBrokers(params: SearchBrokersParams): Promise<{
    success: boolean;
    data?: Broker[];
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('id', params.id);
      queryParams.append('productCode', params.productCode);

      const response = await this.get(
        `/portal/v1/parties/brokers/search?${queryParams.toString()}`,
        {},
        this.customBaseUrl
      );

      const json = await this.handleJsonResponse<Broker[]>(response);

      return json;
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao buscar corretores',
      };
    }
  }

  async getBrokerUsers(
    brokerId: string,
    params: GetBrokerUsersParams = {}
  ): Promise<{
    success: boolean;
    data?: BrokerUsersApiResponse;
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.filter) queryParams.append('filter', params.filter);
      if (params.sortType) queryParams.append('sortType', params.sortType);
      if (params.sortField) queryParams.append('sortField', params.sortField);
      if (params.pageSize)
        queryParams.append('pageSize', params.pageSize.toString());
      if (params.pageNumber)
        queryParams.append('pageNumber', params.pageNumber.toString());

      const search = queryParams.toString();

      const response = await this.get(
        `/portal/v1/parties/brokers/${brokerId}/users${search ? `?${search}` : ''}`,
        {},
        this.customBaseUrl
      );
      const json =
        await this.handleJsonResponse<BrokerUsersApiResponse>(response);

      return json;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getBrokerProducts(
    brokerId: string,
    params?: {
      filter?: string;
      sortType?: string;
      sortField?: string;
      pageSize?: number;
      pageNumber?: number;
    }
  ): Promise<{
    success: boolean;
    data?: {
      data: BrokerProduct[];
      meta: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.filter) queryParams.append('filter', params.filter);
      if (params?.sortType) queryParams.append('sortType', params.sortType);
      if (params?.sortField) queryParams.append('sortField', params.sortField);
      if (params?.pageSize)
        queryParams.append('pageSize', params.pageSize.toString());
      if (params?.pageNumber)
        queryParams.append('pageNumber', params.pageNumber.toString());

      const queryString = queryParams.toString();
      const url = `/portal/v1/parties/brokers/${brokerId}/products${
        queryString ? `?${queryString}` : ''
      }`;

      const response = await this.get(url, {}, this.customBaseUrl);
      const json =
        await this.handleJsonResponse<BrokerProductsApiResponse>(response);

      if (!json.success || !json.data) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar produtos',
        };
      }

      const transformedData = {
        data: json.data.data
          .filter(product => product.productId)
          .map(product => ({
            id: product.id,
            // A API pode omitir o nome; o código identifica o produto de todo
            // jeito e é melhor do que renderizar vazio.
            name: product.productName ?? product.productCode,
            code: product.productCode,
            status: product.status.toLowerCase() as 'active' | 'inactive',
            category: product.category as BrokerProductCategory,
            partyId: product.partyId,
            productCode: product.productCode,
            productId: product.productId,
            productTypeId: product.productTypeId,
            description: product.productDescription,
          })),
        meta: {
          page: json.data.meta.pageNumber,
          pageSize: json.data.meta.pageSize,
          total: json.data.meta.total,
          totalPages: json.data.meta.totalPages,
        },
      };

      return {
        success: true,
        data: transformedData,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async updateBrokerProducts(
    brokerId: string,
    // O corpo do PUT é o DTO de atualização, não a leitura da API: os campos
    // são opcionais e `status` é o par ACTIVE/INACTIVE.
    products: UpdateBrokerProductDto[]
  ): Promise<{
    success: boolean;
    data?: UpdateBrokerProductsResult;
    error?: string;
  }> {
    try {
      const response = await this.post(
        `/portal/v1/parties/brokers/${brokerId}/products`,
        products,
        this.customBaseUrl
      );
      const json =
        await this.handleJsonResponse<UpdateBrokerProductsResult>(response);

      return json;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /**
   * Aplica os MESMOS produtos a varias corretoras de uma vez.
   *
   * Exclusivo do Administrador da seguradora. O endpoint tem teto de itens por
   * chamada (ver BULK_CHUNK_SIZE): quem fatia e o chamador.
   */
  async bulkUpdateBrokerProducts(
    request: BulkUpdateBrokerProductsRequest
  ): Promise<{
    success: boolean;
    data?: BulkUpdateBrokerProductsResult;
    error?: string;
  }> {
    try {
      const response = await this.post(
        '/portal/v1/parties/brokers/products/bulk',
        request,
        this.customBaseUrl
      );

      return await this.handleJsonResponse<BulkUpdateBrokerProductsResult>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }
}

export const brokersService = new BrokersService();
